import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable, OperatorFunction, Subject, debounceTime, distinctUntilChanged, filter, map, merge, shareReplay, takeWhile } from 'rxjs';
import { HelperFunctions } from 'src/app/util/util-functions';

export interface FilterOption<T> {
  prop: string;
  value: T;
}
@Component({
  selector: 'app-typeahead',
  templateUrl: './typeahead.component.html',
  standalone: false
})
export class TypeaheadComponent<T> implements OnDestroy, OnInit {

  @Input() resetInput = false;
  @Input() editable = true;
  @Input() control: FormControl;
  @Input() prop: string;
  @Input() searchOptions: Observable<T[]>;
  @Input() classes = "typeahead-relative";
  @Input() filterOptions: FilterOption<unknown>;
  @Output() readonly selectEvent = new EventEmitter<T>();
  @ViewChild('instance', { static: true }) instance: NgbTypeahead;

  private originalSearchOptions: T[];
  private alive = true;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  public ngOnInit(): void {
    this.searchOptions.pipe(takeWhile(() => this.alive), shareReplay(1))
      .pipe(takeWhile(() => this.alive))
      .subscribe((results: T[]) => {
        let currentResults = results;
        if (this.filterOptions && this.prop) {
          // skipcq: JS-0323
          currentResults = results.filter((options: any) => options[this.filterOptions.prop] === this.filterOptions.value)
        }
        this.originalSearchOptions = currentResults;
      });
  }

  // skipcq: JS-0323
  public formatter = (result: any): string => this.prop ? result[this.prop] : result;
  /**
* A function that search the value in the filtered option list
* @returns { OperatorFunction<string, T[]>}
*/
  search: OperatorFunction<string, readonly T[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      // skipcq: JS-0323
      map((term) => (term === '' ? this.originalSearchOptions : this.originalSearchOptions.filter((v: any) => {
        const value: string = this.prop ? v[this.prop] : v;
        return value.toLowerCase().includes(term.toLowerCase())
      }))));
  };

  onSelect(event: NgbTypeaheadSelectItemEvent) {
    const item = event.item;
    this.selectEvent.emit(item);
    if (this.resetInput)
      event.preventDefault();
  }

  /**
  * A function that checks if the control is invalid
  * @returns {boolean}
  */
  hasErrors(): boolean {
    return HelperFunctions.controlIsInvalid(this.control);
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
}
